/**
 * Pagina di accesso / registrazione.
 *
 * Doppia modalità sullo stesso form: il sistema nasce single-user, quindi la
 * registrazione non merita una pagina propria — serve una volta sola.
 */

import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { ThemeToggle } from '@/app/ThemeToggle';
import { Alert, Button, Field, Logo } from '@/ui';
import { useAuth } from './auth-context';
import './login.css';

type Mode = 'login' | 'register';

export function LoginPage() {
  const { isAuthenticated, isInitializing, login, register } = useAuth();
  const location = useLocation();

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (isAuthenticated && !isInitializing) {
    // Torna alla pagina che aveva richiesto l'autenticazione, se c'era.
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setFieldErrors({});
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      if (mode === 'login') {
        await login({ email, password });
      } else {
        await register({
          email,
          password,
          ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        });
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Gli errori di validazione si mostrano sul campo che li ha generati.
        if (caught.isValidation && caught.details.length > 0) {
          setFieldErrors(
            Object.fromEntries(caught.details.map((detail) => [detail.path, detail.message])),
          );
          setError('Controlla i campi evidenziati.');
        } else {
          setError(caught.message);
        }
      } else {
        setError('Si è verificato un errore imprevisto.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login">
      {/* Anche prima di entrare: chi arriva qui di notte non deve subire la
          schermata chiara per tutta la durata dell'accesso. */}
      <div className="login__theme">
        <ThemeToggle />
      </div>

      <div className="login__panel">
        <header className="login__header">
          <span className="login__logo">
            <Logo size={64} title="LabInvestment" />
          </span>
          <h1 className="login__title">LabInvestment</h1>
          <p className="login__tagline">
            Tracciamento di portafogli di investimento multi-piattaforma
          </p>
        </header>

        <div className="login__tabs" role="tablist" aria-label="Modalità di accesso">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={`login__tab ${mode === 'login' ? 'login__tab--active' : ''}`}
            onClick={() => switchMode('login')}
          >
            Accedi
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={`login__tab ${mode === 'register' ? 'login__tab--active' : ''}`}
            onClick={() => switchMode('register')}
          >
            Crea account
          </button>
        </div>

        <form className="login__form" onSubmit={handleSubmit} noValidate>
          {error && <Alert variant="error">{error}</Alert>}

          {mode === 'register' && (
            <Field
              label="Nome visualizzato"
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              error={fieldErrors['displayName']}
              hint="Facoltativo"
            />
          )}

          <Field
            label="Email"
            type="email"
            required
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={fieldErrors['email']}
          />

          <Field
            label="Password"
            type="password"
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={fieldErrors['password']}
            hint={mode === 'register' ? 'Almeno 12 caratteri' : undefined}
          />

          <Button type="submit" block loading={submitting}>
            {mode === 'login' ? 'Accedi' : 'Crea account e accedi'}
          </Button>
        </form>

        <footer className="login__footer">
          {mode === 'login' ? (
            <p>
              Primo avvio?{' '}
              <button type="button" className="login__link" onClick={() => switchMode('register')}>
                Crea il primo account
              </button>
            </p>
          ) : (
            <p>
              Hai già un account?{' '}
              <button type="button" className="login__link" onClick={() => switchMode('login')}>
                Accedi
              </button>
            </p>
          )}
          <p className="login__note">
            I dati restano sul tuo server: LabInvestment non si collega ad alcun broker.
          </p>
        </footer>
      </div>
    </main>
  );
}
